import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { values } from 'lodash';
import {
  SEMANTIC_SORT_FIELDS,
  SEMANTIC_SORT_RELEVANCE,
  SemanticSearchMethod,
  SemanticSortField,
  SortField,
} from 'utils/enum';
import { Pagination } from 'utils/models';
import { validPageSize } from 'utils/libraryPagination.util';
import { getLocalStorageFilter, IFilterConditioner } from 'utils/utils';

export const readLocal = (field: keyof IFilterConditioner) => {
  return getLocalStorageFilter()?.[field];
};

export const validateSortField = (value: any): SortField => {
  if (!values(SortField).includes(value as SortField)) {
    return SortField.MODIFIED;
  }

  return value as SortField;
};

export const validateSemanticSortField = (value: any): SemanticSortField =>
  SEMANTIC_SORT_FIELDS.includes(value) ? value : SEMANTIC_SORT_RELEVANCE;

const paginationSlice = createSlice({
  name: 'layers',
  initialState: {
    isListView: readLocal('isListView') ?? false,
    page_number: readLocal('page_number') ?? 1,
    page_size: validPageSize(Number(readLocal('page_size') ?? 30), false),
    sortBy: validateSortField(readLocal('sortBy')) ?? SortField.MODIFIED,
    isAsc: readLocal('isAsc') ?? (false as boolean),
    // Semantic search's own sort, independent of sortBy / isAsc above. Not
    // persisted: every session starts on the engine's rank.
    semanticSortBy: SEMANTIC_SORT_RELEVANCE as SemanticSortField,
    semanticIsAsc: false,
    semanticSearchMethod: SemanticSearchMethod.ALL,
    isLoaded: true as boolean,
  } as Pagination,
  reducers: {
    setPagination: (state, action: PayloadAction<Pagination>) => {
      return { ...state, ...action.payload };
    },
    changePage: (state, action) => {
      state.page_number = action.payload.page_number;
      state.page_size = action.payload.page_size;
    },
    setPageNumber: (state, { payload }: PayloadAction<number>) => {
      state.page_number = payload;
    },
    setPageSize: (state, { payload }: PayloadAction<number>) => {
      state.page_size = payload;
    },
    changeLoading: (state, action) => {
      state.isLoaded = action.payload;
    },
    changeSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = validateSortField(action.payload);
      state.page_number = 1;
    },
    changeOrder: (state, action: PayloadAction<boolean>) => {
      state.isAsc = action.payload;
    },
    toggleSortOrder: (state) => {
      state.isAsc = !state.isAsc;
    },
    changeSemanticSortBy: (state, action: PayloadAction<string>) => {
      state.semanticSortBy = validateSemanticSortField(action.payload);
      state.page_number = 1;
    },
    toggleSemanticSortOrder: (state) => {
      state.semanticIsAsc = !state.semanticIsAsc;
    },
    changeSemanticOrder: (state, action: PayloadAction<boolean>) => {
      state.semanticIsAsc = action.payload;
    },
    changeSemanticSearchMethod: (
      state,
      action: PayloadAction<SemanticSearchMethod>,
    ) => {
      state.semanticSearchMethod = action.payload;
      state.page_number = 1;
    },
    setIsListView: (state, action: PayloadAction<boolean>) => {
      state.isListView = action.payload;
    },
  },
});

export const {
  setPagination,
  changePage,
  changeLoading,
  changeSortBy,
  changeOrder,
  toggleSortOrder,
  changeSemanticSortBy,
  toggleSemanticSortOrder,
  changeSemanticOrder,
  changeSemanticSearchMethod,
  setIsListView,
  setPageNumber,
  setPageSize,
} = paginationSlice.actions;

export const paginationReducer = paginationSlice.reducer;
